<template>
  <div class="flex flex-col gap-8 md:gap-12">
    <article
      v-for="(category, index) in categories"
      :key="category.id"
      class="grid items-center gap-5 border-b border-default pb-8 last:border-b-0 last:pb-0 md:grid-cols-2 md:gap-12 md:pb-12"
    >
      <NuxtLink
        :to="shopPath(`/categorii/${category.slug}`)"
        class="group relative overflow-hidden rounded-[1.75rem] bg-muted ring-1 ring-black/5"
        :class="index % 2 === 1 ? 'md:order-2' : ''"
        :style="{ height: `${imageHeight}px` }"
        :aria-label="`Vezi ${category.name}`"
        @mouseenter="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
        @focus="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
      >
        <AppImage
          v-if="category.image"
          :src="category.image"
          :alt="category.name"
          preset="thumbnail"
          width="800"
          height="600"
          sizes="xs:100vw sm:100vw md:50vw lg:50vw"
          densities="x1 x2"
          :loading="index === 0 ? 'eager' : 'lazy'"
          :fetchpriority="index === 0 ? 'high' : 'auto'"
          :preload="index === 0"
          class="h-full w-full object-cover transition duration-700 motion-safe:group-hover:scale-[1.04]"
        />
        <div v-else class="h-full w-full bg-gradient-to-br from-brand-400 to-brand-700" />
      </NuxtLink>

      <div class="min-w-0" :class="index % 2 === 1 ? 'md:order-1 md:text-right' : ''">
        <p class="font-mono text-xs font-medium tracking-[0.22em] text-brand-600">
          {{ indexLabel(index) }}
        </p>
        <NuxtLink
          :to="shopPath(`/categorii/${category.slug}`)"
          class="mt-2 block text-3xl font-semibold tracking-tight text-highlighted transition-colors hover:text-brand-600 md:text-5xl"
          @mouseenter="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
          @focus="prefetchRoute(shopPath(`/categorii/${category.slug}`))"
        >
          {{ category.name }}
        </NuxtLink>
        <p v-if="plainDescription(category.description)" class="mt-3 line-clamp-3 text-sm leading-relaxed text-toned md:text-base">
          {{ plainDescription(category.description) }}
        </p>
        <p v-if="category.productCount" class="mt-3 text-sm text-muted">
          {{ productLabel(category.productCount) }}
        </p>

        <div
          v-if="visibleChildren(category).length"
          class="mt-4 flex flex-wrap gap-2"
          :class="index % 2 === 1 ? 'md:justify-end' : ''"
        >
          <NuxtLink
            v-for="child in visibleChildren(category)"
            :key="child.id"
            :to="shopPath(`/categorii/${child.slug}`)"
            class="rounded-full border border-default px-3 py-1 text-sm text-highlighted transition hover:border-brand-400 hover:text-brand-700"
            @mouseenter="prefetchRoute(shopPath(`/categorii/${child.slug}`))"
            @focus="prefetchRoute(shopPath(`/categorii/${child.slug}`))"
          >
            {{ child.name }}
          </NuxtLink>
          <span
            v-if="extraChildCount(category) > 0"
            class="self-center text-xs text-muted"
          >
            +{{ extraChildCount(category) }}
          </span>
        </div>

        <NuxtLink
          :to="shopPath(`/categorii/${category.slug}`)"
          class="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600"
        >
          Vezi categoria
          <UIcon name="i-heroicons-arrow-right" class="h-4 w-4" />
        </NuxtLink>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import type { Category } from '~/types'

defineProps<{
  categories: Category[]
  imageHeight: number
}>()

const shopPath = useShopPath()
const { prefetchRoute } = useRoutePrefetch()

const childLimit = 6

function indexLabel(index: number) {
  return String(index + 1).padStart(2, '0')
}

function plainDescription(value?: string) {
  return (value ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function productLabel(count: number) {
  return `${count} ${count === 1 ? 'produs' : 'produse'}`
}

function visibleChildren(category: Category) {
  return (category.children ?? []).slice(0, childLimit)
}

function extraChildCount(category: Category) {
  return Math.max(0, (category.children?.length ?? 0) - childLimit)
}
</script>
